import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Card,
    Row,
    Col,
    Form,
    Select,
    DatePicker,
    Button,
    Typography,
    Divider,
    Tag,
    notification,
    Breadcrumb,
    Space,
    Spin,
} from 'antd';
import {
    HomeOutlined,
    SafetyCertificateOutlined,
    DownloadOutlined,
    BarChartOutlined,
} from '@ant-design/icons';
import dayjs from 'dayjs';
import PizZip from 'pizzip';
import { saveAs } from 'file-saver';
import { customerService } from '../services/customerService';
import { orderService } from '../services/orderService';

const { Title, Text } = Typography;
const { Option } = Select;

/* ── XML helpers ─────────────────────────────────────── */
const escapeXml = (str) => {
    if (!str && str !== 0) return '';
    return String(str)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;')
        .replace(/"/g, '&quot;')
        .replace(/'/g, '&apos;');
};

const replaceFirst = (str, search, replacement) => {
    const idx = str.indexOf(search);
    if (idx === -1) return str;
    return str.slice(0, idx) + replacement + str.slice(idx + search.length);
};

/* ── Page ─────────────────────────────────────────────── */
const WarrantyCertificatePage = () => {
    const navigate = useNavigate();
    const [form] = Form.useForm();

    // All customers (loaded once on mount)
    const [customers, setCustomers] = useState([]);
    const [loadingCustomers, setLoadingCustomers] = useState(false);
    const [selectedCustomer, setSelectedCustomer] = useState(null);

    // Orders for selected customer
    const [orders, setOrders] = useState([]);
    const [loadingOrders, setLoadingOrders] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);

    // Certificate generation
    const [generating, setGenerating] = useState(false);

    // Watched form value for live end-date preview
    const startDateValue = Form.useWatch('startDate', form);
    const endDateValue = startDateValue ? dayjs(startDateValue).add(5, 'year') : null;

    /* Load only customers who have at least one Closed order */
    useEffect(() => {
        const fetchEligibleCustomers = async () => {
            try {
                setLoadingCustomers(true);
                const [custRes, ordersRes] = await Promise.all([
                    customerService.getAllCustomers({ limit: 1000 }),
                    orderService.getAllOrders({ limit: 1000 }),
                ]);
                const allCustomers = custRes?.data || custRes || [];
                const allOrders = ordersRes?.data || ordersRes || [];
                const closedCustomerIds = new Set(
                    allOrders
                        .filter(o => (o.projectStatus === 'Closed' || o.projectStatus === 'Completed'))
                        .map(o => o.customerId)
                );
                const eligibleCustomers = allCustomers.filter(c => closedCustomerIds.has(c.id));
                setCustomers(eligibleCustomers);
            } catch {
                notification.error({ message: 'Failed to load customers' });
            } finally {
                setLoadingCustomers(false);
            }
        };
        fetchEligibleCustomers();
    }, []);

    /* When user picks a customer, fetch their orders */
    const handleCustomerSelect = async (customerId) => {
        const cust = customers.find((c) => c.id === customerId) || null;
        setSelectedCustomer(cust);
        setSelectedOrder(null);
        setOrders([]);
        form.setFieldsValue({ orderId: undefined, startDate: undefined });

        if (!customerId) return;

        try {
            setLoadingOrders(true);
            const res = await orderService.getAllOrders({ customerId, limit: 100 });
            const allOrders = res?.data || res || [];
            const closedOrders = allOrders.filter(o => (o.projectStatus === 'Closed' || o.projectStatus === 'Completed'));
            setOrders(closedOrders);
        } catch {
            notification.error({ message: 'Failed to fetch orders for this customer' });
        } finally {
            setLoadingOrders(false);
        }
    };

    const handleOrderChange = (orderId) => {
        const order = orders.find((o) => o.id === orderId) || null;
        setSelectedOrder(order);
    };

    /* Generate the PPTX */
    const handleGenerate = async () => {
        try {
            const values = await form.validateFields();
            setGenerating(true);

            const startDate = values.startDate;
            const endDate = startDate.add(5, 'year');

            const customerName = selectedCustomer?.customerName || '';
            const district = selectedCustomer?.district || '';
            const consumerNo = selectedOrder?.selectedConsumerNumber || selectedCustomer?.consumerNo || '';
            const plantCapacity = selectedOrder?.plantSizeKw != null ? selectedOrder.plantSizeKw : '';

            const startDateStr = startDate.format('DD/MM/YYYY');
            const endDateStr = endDate.format('DD/MM/YYYY');

            // Load template
            const templateResponse = await fetch('/templates/warranty_certificate_template.pptx');
            if (!templateResponse.ok) {
                throw new Error('Template file not found. Check public/templates/warranty_certificate_template.pptx');
            }
            const templateBuffer = await templateResponse.arrayBuffer();

            // Unzip, patch slide XML, re-zip
            const zip = new PizZip(templateBuffer);
            const slideFile = zip.files['ppt/slides/slide1.xml'];
            if (!slideFile) throw new Error('Invalid template: slide1.xml missing');

            let xml = slideFile.asText();

            xml = replaceFirst(xml, 'Customer Name        -</a:t>', `Customer Name        - ${escapeXml(customerName)}</a:t>`);
            xml = replaceFirst(xml, 'Customer Address   -</a:t>',   `Customer Address   - ${escapeXml(district)}</a:t>`);
            xml = replaceFirst(xml, 'Consumer No.             -</a:t>', `Consumer No.             - ${escapeXml(consumerNo)}</a:t>`);
            xml = replaceFirst(xml, 'RTS Plant  Capacity  - </a:t>', `RTS Plant  Capacity  - ${escapeXml(String(plantCapacity))} KW </a:t>`);
            // First occurrence → start date
            xml = replaceFirst(xml, 'Warranty Start Date    - </a:t>', `Warranty Start Date    - ${escapeXml(startDateStr)} </a:t>`);
            // Second occurrence → end date (also change the label)
            xml = replaceFirst(xml, 'Warranty Start Date    - </a:t>', `Warranty End Date      - ${escapeXml(endDateStr)} </a:t>`);


            // Fix alignment for Customer Name/Address shape:
            // Text box starts at x=-1309508 (outside slide). Use lIns to push text
            // into the visible bordered area + left-align + remove leading spaces
            // Customer Name shape: position text at left edge of bordered area using lIns
            xml = xml.replace(
                /(<p:sp>[\s\S]*?Customer Name[\s\S]*?<\/p:sp>)/g,
                (shapeMatch) => shapeMatch
                    .replace(/lIns="0"/, 'lIns="2560000"')
                    .replace(/algn="ctr"/g, 'algn="l"')
                    .replace(/(<a:t>)\s+/g, '$1')
            );

            // Consumer No shape: shift text to the right so it doesn't overlap with long customer names
            xml = xml.replace(
                /(<p:sp>[\s\S]*?Consumer No\.[\s\S]*?<\/p:sp>)/g,
                (shapeMatch) => shapeMatch
                    .replace(/lIns="0"/, 'lIns="1500000"')
                    .replace(/algn="ctr"/g, 'algn="l"')
                    .replace(/(<a:t>)\s+/g, '$1')
            );

            zip.file('ppt/slides/slide1.xml', xml);

            const blob = zip.generate({
                type: 'blob',
                mimeType: 'application/vnd.openxmlformats-officedocument.presentationml.presentation',
            });

            const safeName = customerName.replace(/[^a-zA-Z0-9\s]/g, '').replace(/\s+/g, '_');
            saveAs(blob, `Warranty_Certificate_${safeName}.pptx`);

            notification.success({
                message: 'Certificate Downloaded',
                description: `Warranty certificate for ${customerName} has been saved.`,
            });
        } catch (error) {
            if (error?.errorFields) return; // Ant Design validation, messages shown inline
            console.error(error);
            notification.error({
                message: 'Generation Failed',
                description: error.message || 'An error occurred.',
            });
        } finally {
            setGenerating(false);
        }
    };

    /* Derived display values */
    const consumerNo = selectedOrder?.selectedConsumerNumber || selectedCustomer?.consumerNo || null;
    const plantCapacity = selectedOrder?.plantSizeKw != null ? `${selectedOrder.plantSizeKw} KW` : null;

    return (
        <div style={{ padding: '24px' }}>
            {/* Breadcrumb */}
            <Breadcrumb style={{ marginBottom: 16 }}>
                <Breadcrumb.Item onClick={() => navigate('/')} style={{ cursor: 'pointer' }}>
                    <HomeOutlined />
                </Breadcrumb.Item>
                <Breadcrumb.Item>
                    <BarChartOutlined /> Reports
                </Breadcrumb.Item>
                <Breadcrumb.Item>Warranty Certificate</Breadcrumb.Item>
            </Breadcrumb>

            {/* Header */}
            <div style={{ marginBottom: 24 }}>
                <Space align="center">
                    <SafetyCertificateOutlined style={{ fontSize: 28, color: '#1890ff' }} />
                    <div>
                        <Title level={3} style={{ margin: 0 }}>Warranty Certificate</Title>
                        <Text type="secondary">Generate a 5-year RTS plant warranty certificate</Text>
                    </div>
                </Space>
            </div>

            <Row gutter={[24, 24]} justify="center">
                <Col xs={24} lg={16} xl={14}>
                    <Card style={{ borderRadius: 12, boxShadow: '0 2px 12px rgba(0,0,0,0.06)' }}>
                        <Form form={form} layout="vertical" requiredMark={false}>

                            {/* ── Customer ── */}
                            <Divider orientation="left" orientationMargin={0}>
                                <Text strong>Customer</Text>
                            </Divider>

                            <Form.Item
                                name="customerId"
                                label="Select Customer"
                                rules={[{ required: true, message: 'Please select a customer' }]}
                            >
                                <Select
                                    showSearch
                                    optionFilterProp="children"
                                    loading={loadingCustomers}
                                    onChange={handleCustomerSelect}
                                    onClear={() => {
                                        setSelectedCustomer(null);
                                        setOrders([]);
                                        setSelectedOrder(null);
                                    }}
                                    allowClear
                                    placeholder="Search customer by name…"
                                    notFoundContent={loadingCustomers ? <Spin size="small" /> : 'No customers found'}
                                    style={{ width: '100%' }}
                                >
                                    {customers.map((c) => (
                                        <Option key={c.id} value={c.id}>
                                            {c.customerName}
                                            {c.district ? ` — ${c.district}` : ''}
                                        </Option>
                                    ))}
                                </Select>
                            </Form.Item>

                            {selectedCustomer && (
                                <Row gutter={16} style={{ marginBottom: 8 }}>
                                    <Col xs={24} sm={12}>
                                        <Form.Item label="District (used as address)" style={{ marginBottom: 8 }}>
                                            <Text strong>{selectedCustomer.district || <Text type="secondary">Not set</Text>}</Text>
                                        </Form.Item>
                                    </Col>
                                </Row>
                            )}

                            {/* ── Order ── */}
                            {selectedCustomer && (
                                <>
                                    <Divider orientation="left" orientationMargin={0}>
                                        <Text strong>Order &amp; Plant Details</Text>
                                    </Divider>

                                    {loadingOrders ? (
                                        <div style={{ textAlign: 'center', padding: '12px 0' }}>
                                            <Spin tip="Loading orders…" />
                                        </div>
                                    ) : orders.length === 0 ? (
                                        <Text type="secondary">No eligible orders found. Only orders with <Text strong>Completed</Text> or <Text strong>Closed</Text> project status can be used to generate a warranty certificate.</Text>
                                    ) : (
                                        <Form.Item
                                            name="orderId"
                                            label="Select Order"
                                            rules={[{ required: true, message: 'Please select an order' }]}
                                        >
                                            <Select
                                                placeholder="Select order to load consumer no. and capacity"
                                                onChange={handleOrderChange}
                                                allowClear
                                            >
                                                {orders.map((o) => (
                                                    <Option key={o.id} value={o.id}>
                                                        Order #{o.id}
                                                        {o.plantSizeKw ? ` — ${o.plantSizeKw} KW` : ''}
                                                        {o.selectedConsumerNumber ? ` — ${o.selectedConsumerNumber}` : ' — No consumer no.'}
                                                    </Option>
                                                ))}
                                            </Select>
                                        </Form.Item>
                                    )}

                                    <Row gutter={16}>
                                        <Col xs={24} sm={12}>
                                            <Form.Item label="Consumer Number" style={{ marginBottom: 8 }}>
                                                {consumerNo
                                                    ? <Tag color="orange" style={{ fontSize: 13 }}>{consumerNo}</Tag>
                                                    : <Text type="secondary">N/A</Text>}
                                            </Form.Item>
                                        </Col>
                                        <Col xs={24} sm={12}>
                                            <Form.Item label="Plant Capacity" style={{ marginBottom: 8 }}>
                                                {plantCapacity
                                                    ? <Tag color="green" style={{ fontSize: 13 }}>{plantCapacity}</Tag>
                                                    : <Text type="secondary">N/A</Text>}
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </>
                            )}

                            {/* ── Warranty Period ── */}
                            <Divider orientation="left" orientationMargin={0}>
                                <Text strong>Warranty Period</Text>
                            </Divider>

                            <Row gutter={16}>
                                <Col xs={24} sm={12}>
                                    <Form.Item
                                        name="startDate"
                                        label="Warranty Start Date"
                                        rules={[{ required: true, message: 'Please select a start date' }]}
                                    >
                                        <DatePicker
                                            format="DD/MM/YYYY"
                                            style={{ width: '100%' }}
                                            placeholder="Select start date"
                                        />
                                    </Form.Item>
                                </Col>
                                <Col xs={24} sm={12}>
                                    <Form.Item label="Warranty End Date (Auto)">
                                        {endDateValue ? (
                                            <div>
                                                <Tag color="blue" style={{ fontSize: 13 }}>
                                                    {endDateValue.format('DD/MM/YYYY')}
                                                </Tag>
                                                <div style={{ fontSize: 11, color: '#8c8c8c', marginTop: 4 }}>
                                                    Start date + 5 years
                                                </div>
                                            </div>
                                        ) : (
                                            <Text type="secondary">Select a start date</Text>
                                        )}
                                    </Form.Item>
                                </Col>
                            </Row>

                            {/* ── Generate ── */}
                            <Form.Item style={{ marginTop: 8, marginBottom: 0 }}>
                                <Button
                                    type="primary"
                                    icon={<DownloadOutlined />}
                                    loading={generating}
                                    onClick={handleGenerate}
                                    size="large"
                                    block
                                >
                                    Generate &amp; Download Certificate
                                </Button>
                            </Form.Item>
                        </Form>
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default WarrantyCertificatePage;
